import { useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { api } from "@/api/client";
import ErrorBanner from "@/components/common/ErrorBanner";
import AlbumTree from "@/components/media/AlbumTree";

/**
 * Add the selected media to an album (membership-add, never removes). Strom alb je tu se
 * správou: založení alba otevře `AlbumDialog` nad tímto modalem (`z-[60]` nad `z-50`).
 */
export default function AddToAlbumModal({
  mediaIds,
  onClose,
  onDone,
}: {
  mediaIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const [target, setTarget] = useState<string | null>(null);
  const addMutation = useMutation({
    mutationFn: (folderId: string) =>
      api.post(`/api/admin/media-folders/${folderId}/items`, { mediaIds }),
    onSuccess: onDone,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[80vh] w-full max-w-sm flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("media.albums.addCountTitle", { count: mediaIds.length })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.actions.close")}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <AlbumTree manage selectedId={target} onSelect={setTarget} />
        </div>
        {addMutation.error && (
          <div className="px-4 pb-2">
            <ErrorBanner error={addMutation.error} />
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">
            {t("common.actions.cancel")}
          </button>
          <button
            type="button"
            onClick={() => target && addMutation.mutate(target)}
            disabled={!target || addMutation.isPending}
            className="btn-primary text-sm"
          >
            {addMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("media.albums.addToAlbum")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
